import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "@/context/AuthContext";
import { TimerProvider } from "@/context/TimerContext";
import { ToastProvider } from "@/context/ToastContext";
import { initTheme, initDensity, initPlanningDefaults } from "@/lib/theme";
import { initPb, demoRequested, type AppMode } from "@/lib/pocketbase";
import App from "./App";
import "./index.css";

initTheme();
initDensity();
initPlanningDefaults();

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, refetchOnWindowFocus: false } },
});

/**
 * Decide demo vs live at runtime. The Worker exposes GET /__mode -> { mode }.
 * Default is "demo", so a fresh deploy is a working playground with no backend.
 * Flip to live by setting APP_MODE=live (and POCKETBASE_URL) on the Worker.
 * A forced build-time flag (VITE_DEMO=1, used by the single-file build) wins.
 */
async function resolveMode(): Promise<AppMode> {
  if (import.meta.env.VITE_DEMO === "1") return "demo";
  // A visitor who chose "Explore the demo" stays in it until they leave.
  if (demoRequested()) return "demo";
  try {
    const r = await fetch("/__mode", { cache: "no-store" });
    if (r.ok) {
      const j = (await r.json()) as { mode?: string };
      if (j.mode === "live") return "live";
    }
  } catch {
    /* no Worker (e.g. plain `vite dev`) — fall through to demo */
  }
  return "demo";
}

async function boot() {
  const mode = await resolveMode();
  initPb(mode);

  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          {/* Toasts sit ABOVE the timer: the timer raises its own (a failed
              save on stop, the long-running nudge), and a useToast() outside
              the provider silently gets a no-op. */}
          <ToastProvider>
            <TimerProvider>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </TimerProvider>
          </ToastProvider>
        </AuthProvider>
      </QueryClientProvider>
    </React.StrictMode>
  );
}

boot();
